// User types
export interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  phone?: string
  phoneNumber?: string
  role: UserRole
  isVerified: boolean
  isApproved: boolean
  /** Temp credential issued (bulk-created/admin reset); must set own password. */
  mustChangePassword?: boolean
  profilePicture?: string
  providerProfile?: ServiceProvider
  createdAt?: string
  updatedAt?: string
  address?: string
  city?: string
  state?: string
  postalCode?: string
  emergencyContactName?: string
  emergencyContactPhone?: string
  dateOfBirth?: string
  gender?: 'MALE' | 'FEMALE'
  preferences?: Record<string, any>
}

export enum UserRole {
  FAMILY = 'FAMILY',
  AMBULANCE_PROVIDER = 'AMBULANCE_PROVIDER',
  USTADZ = 'USTADZ',
  CEMETERY_PROVIDER = 'CEMETERY_PROVIDER',
  VENDOR = 'VENDOR',
  ADMIN = 'ADMIN',
  FINANCE = 'FINANCE',
}

export interface ServiceProvider {
  id: string
  userId: string
  user?: User
  companyName: string
  serviceType: ServiceType
  description?: string
  address?: string
  city?: string
  state?: string
  postalCode?: string
  coordinates?: { lat: number; lng: number }
  serviceAreas: any[]
  pricing?: any
  availability?: any
  certifications: any[]
  rating?: number
  reviewCount: number
  isActive: boolean
}

export enum ServiceType {
  AMBULANCE_TRANSPORT = 'AMBULANCE_TRANSPORT',
  GHUSL_SERVICE = 'GHUSL_SERVICE',
  KAFAN_SERVICE = 'KAFAN_SERVICE',
  JANAZAH_LEAD = 'JANAZAH_LEAD',
  BURIAL_PLOT = 'BURIAL_PLOT',
  CATERING = 'CATERING',
  FLOWERS = 'FLOWERS',
  TENT_RENTAL = 'TENT_RENTAL',
  PHOTOGRAPHY = 'PHOTOGRAPHY',
  YASIN_BOOKS = 'YASIN_BOOKS',
  ISLAMIC_SOUVENIRS = 'ISLAMIC_SOUVENIRS',

  // Provider types
  AMBULANCE_PROVIDER = 'AMBULANCE_PROVIDER',
  USTADZ = 'USTADZ',
  CEMETERY_PROVIDER = 'CEMETERY_PROVIDER',
  FUNERAL_HOME = 'FUNERAL_HOME',
  VENDOR = 'VENDOR',
  PROVIDER = 'PROVIDER',
}

// Funeral Case types
export interface FuneralCase {
  id: string
  caseNumber: string
  familyId: string
  family: User
  deceasedName: string
  deceasedGender: Gender
  deceasedAge?: number
  deceasedIC?: string
  placeOfDeath?: string
  timeOfDeath?: string
  causeOfDeath?: string
  primaryContact: string
  primaryContactPhone: string
  secondaryContact?: string
  secondaryContactPhone?: string
  status: CaseStatus
  priorityLevel?: string
  notes?: string
  estimatedTotal?: number
  actualTotal?: number
  preferredBurialDate?: string
  preferredBurialTime?: string
  mosquePreference?: string
  pickupAddress?: string
  pickupCoordinates?: { lat: number; lng: number }
  burialCemetery?: string
  burialPlot?: string
  createdAt: string
  updatedAt: string
  documents: Document[]
  bookings: Booking[]
  payments: Payment[]
}

export enum CaseStatus {
  INITIATED = 'INITIATED',
  BOOKING_APPROVED = 'BOOKING_APPROVED',
  PAYMENT_MADE = 'PAYMENT_MADE',
  PAYMENT_APPROVED = 'PAYMENT_APPROVED',
  DOCUMENTS_PENDING = 'DOCUMENTS_PENDING',
  TRANSPORT_SCHEDULED = 'TRANSPORT_SCHEDULED',
  GHUSL_SCHEDULED = 'GHUSL_SCHEDULED',
  KAFAN_SCHEDULED = 'KAFAN_SCHEDULED',
  JANAZAH_SCHEDULED = 'JANAZAH_SCHEDULED',
  BURIAL_SCHEDULED = 'BURIAL_SCHEDULED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export enum Gender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
}

// Document types
export interface Document {
  id: string
  caseId: string
  type: DocumentType
  fileName: string
  originalName: string
  filePath: string
  fileSize: number
  mimeType: string
  status: DocumentStatus
  verificationNotes?: string
  uploadedAt: string
  verifiedAt?: string
  verifiedBy?: string
  case?: {
    id: string
    caseNumber: string
    deceasedName: string
    family: {
      id: string
      firstName: string
      lastName: string
      phone?: string
    }
  }
}

export interface ServiceProviderDocument {
  id: string
  providerId: string
  type: DocumentType
  fileName: string
  originalName: string
  filePath: string
  fileSize: number
  mimeType: string
  status: DocumentStatus
  verificationNotes?: string | null
  uploadedAt: string
  verifiedAt?: string | null
  verifiedBy?: string | null
  serviceProvider: ServiceProvider
}

export interface UserDocument {
  id: string
  userId: string
  type: DocumentType
  fileName: string
  originalName: string
  filePath: string
  fileSize: number
  mimeType: string
  status: DocumentStatus
  verificationNotes?: string | null
  uploadedAt: string
  verifiedAt?: string | null
  verifiedBy?: string | null
  user?: {
    id: string
    memberId?: string
    firstName: string
    lastName: string
    email: string
    phone?: string
  }
}

export enum DocumentType {
  // Funeral case documents
  MEDICAL_CERTIFICATE = 'MEDICAL_CERTIFICATE',
  BURIAL_PERMIT = 'BURIAL_PERMIT',
  IDENTIFICATION = 'IDENTIFICATION',
  CONSENT_FORM = 'CONSENT_FORM',

  // Provider verification documents
  BUSINESS_LICENSE = 'BUSINESS_LICENSE',
  PROFESSIONAL_CERTIFICATE = 'PROFESSIONAL_CERTIFICATE',
  SERVICE_CERTIFICATION = 'SERVICE_CERTIFICATION',
  INSURANCE_DOCUMENT = 'INSURANCE_DOCUMENT',
  OPERATING_PERMIT = 'OPERATING_PERMIT',
  TAX_CLEARANCE = 'TAX_CLEARANCE',
  DIRECTOR_IDENTIFICATION = 'DIRECTOR_IDENTIFICATION',
  COMPANY_REGISTRATION = 'COMPANY_REGISTRATION',

  OTHER = 'OTHER',

  // User / provider identity & compliance documents
  IC_PASSPORT = 'IC_PASSPORT',
  SSM_CERTIFICATE = 'SSM_CERTIFICATE',
  UTILITY_BILL = 'UTILITY_BILL',
  SELFIE_PHOTO = 'SELFIE_PHOTO',
  BIRTH_CERTIFICATE = 'BIRTH_CERTIFICATE',
  PROOF_OF_ADDRESS = 'PROOF_OF_ADDRESS',
}

export enum DocumentStatus {
  PENDING = 'PENDING',
  UPLOADED = 'UPLOADED',
  VERIFIED = 'VERIFIED',
  REJECTED = 'REJECTED',
}

// Booking types
export interface Booking {
  id: string
  caseId: string
  providerId: string
  provider: ServiceProvider
  serviceType: ServiceType
  scheduledDateTime: string
  duration?: number
  location?: string
  coordinates?: { lat: number; lng: number }
  specialRequests?: string
  pricing?: any
  status: BookingStatus
  confirmedAt?: string
  completedAt?: string
  notes?: string
  createdAt: string
  updatedAt: string
  review?: Review
}

export enum BookingStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export interface Review {
  id: string
  bookingId: string
  providerId: string
  caseId: string
  rating: number
  comment?: string
  isPublic: boolean
  createdAt: string
  updatedAt: string
}

// Payment types
export interface Payment {
  id: string
  caseId: string
  userId: string
  description: string
  amount: number
  status: PaymentStatus
  paymentMethod?: string
  transactionId?: string
  gatewayResponse?: any
  dueDate?: string
  paidAt?: string
  createdAt: string
  updatedAt: string
  caseNumber?: string
  user?: {
    id: string
    firstName: string
    lastName: string
    email: string
    phone?: string
  }
  funeral_cases?: {
    caseNumber: string
    deceasedName?: string
  }
  transferProofUrl?: string
  receiptUrl?: string
  verificationNotes?: string
  rejectionReason?: string
}

export enum PaymentStatus {
  PENDING = 'PENDING',
  PARTIAL = 'PARTIAL',
  PAID = 'PAID',
  REFUNDED = 'REFUNDED',
}

// API Response types
export interface ApiResponse<T> {
  data: T
  message?: string
  success: boolean
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  limit: number
  totalPages: number
}

// Form types
export interface LoginCredentials {
  email: string
  password: string
}

// Two-factor authentication (TOTP)
export interface AuthResponse {
  access_token: string
  user: User
  message?: string
}

export interface TwoFactorLoginResponse {
  twoFactorRequired: true
  challengeToken: string
}

export type LoginResponse = AuthResponse | TwoFactorLoginResponse

export interface TwoFactorStatus {
  enabled: boolean
}

export interface TwoFactorSetupResponse {
  otpauthUrl: string
  secret: string
  expiresInMinutes: number
}

export interface TwoFactorEnableResponse {
  message: string
  recoveryCodes: string[]
}

export interface VerifyTwoFactorParams {
  challengeToken: string
  code?: string
  recoveryCode?: string
}

export interface RegisterFamilyData {
  email: string
  password: string
  firstName: string
  lastName: string
  phone: string
  nationality: string
  passportNumber?: string
  referralCode?: string
}

export interface CreateCaseData {
  deceasedName: string
  deceasedGender: Gender
  deceasedAge?: number
  deceasedIC?: string
  placeOfDeath?: string
  timeOfDeath?: string
  causeOfDeath?: string
  primaryContact: string
  primaryContactPhone: string
  secondaryContact?: string
  secondaryContactPhone?: string
  preferredBurialDate?: string
  preferredBurialTime?: string
  mosquePreference?: string
  pickupAddress?: string
  pickupCoordinates?: { lat: number; lng: number }
  burialCemetery?: string
  burialPlot?: string
  notes?: string
}